import React, { useState, useEffect } from 'react';
import { UserData, updateSubscription as apiUpdateSubscription } from '../api/admin';
import { useUserSubscriptions } from '../hooks/queries/useAdmin';
import { useQueryClient } from '@tanstack/react-query';

interface AdminUserDialogProps {
  user: UserData;
  onClose: () => void;
  onUpdate: (userId: string, field: string, value: any) => Promise<void>;
  onResetPassword: (user: UserData) => void;
  onDeleteUser: (user: UserData) => void;
  onTestPush: (userId: string) => void;
  onTestEmail: (userId: string) => void;
  onTestTelegram: (userId: string) => void;
  onEditSubscription: (sub: any) => void;
  onResetEmailVerification: (userId: string) => Promise<void>;
  isResetting: boolean;
  isDeleting: boolean;
  isSendingEmail?: boolean;
  isSendingTelegram?: boolean;
  isSendingPush?: boolean;
  /** When true, ESC key is suppressed (e.g. a child modal is open on top) */
  isBlocked?: boolean;
}

export const AdminUserDialog: React.FC<AdminUserDialogProps> = ({
  user, onClose, onUpdate, onResetPassword, onDeleteUser, onTestPush, onTestEmail, onTestTelegram, onEditSubscription, onResetEmailVerification,
  isResetting, isDeleting, isSendingEmail, isSendingTelegram, isSendingPush, isBlocked,
}) => {
  const { data: userSubsData, isLoading: isLoadingSubs } = useUserSubscriptions(user.userId, true);
  const subscriptions = userSubsData?.subscriptions || [];

  const [localChanges, setLocalChanges] = useState<Partial<UserData>>({});
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const [isResettingEmailVerif, setIsResettingEmailVerif] = useState(false);
  const isModified = Object.keys(localChanges).length > 0;
  const queryClient = useQueryClient();

  // Filter subscriptions by search term
  const filteredSubscriptions = subscriptions.filter((sub: any) => {
    const searchLower = searchFilter.toLowerCase().trim();
    if (!searchLower) return true;

    const parts: string[] = [];

    // Add zone/ZIP info
    if (sub.uiMetadata?.zones && sub.uiMetadata.zones.length > 0) {
      sub.uiMetadata.zones.forEach((z: any) => {
        parts.push((z.l || z.label || z.i || z.id || '').toLowerCase());
      });
    }
    if (sub.zipCodes?.length > 0) {
      parts.push(...sub.zipCodes.map((z: string) => z.toLowerCase()));
    }

    // Add telegram info
    if (sub.notificationTargets?.telegram) {
      const tg = sub.notificationTargets.telegram;
      if (tg.chatTitle) parts.push(tg.chatTitle.toLowerCase());
      if (tg.chatId) parts.push(String(tg.chatId).toLowerCase());
    }

    // Check if any part matches
    return parts.some(part => part.includes(searchLower));
  });

  const handleExpiryChange = async (subscriptionId: string, expiresAt: number | null) => {
    try {
      await apiUpdateSubscription(subscriptionId, { expiresAt });
      queryClient.invalidateQueries({ queryKey: ['admin', 'userSubscriptions', user.userId] });
    } catch (err) {
      console.error('Failed to update expiry', err);
    }
  };

  // Escape key closes dialog (suppressed when a child modal is open on top)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isBlocked) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, isBlocked]);

  const handleLocalChange = (field: keyof UserData, value: any) => {
    // Normalize: treat empty string as null for nullable fields like email
    const currentValue = (user as any)[field];
    const normalizedValue = value;
    const normalizedCurrent = currentValue === null ? '' : currentValue;
    
    if (normalizedValue === normalizedCurrent) {
      const next = { ...localChanges };
      delete (next as any)[field];
      setLocalChanges(next);
    } else {
      setLocalChanges(prev => ({ ...prev, [field]: value }));
    }
  };

  const handleSave = async () => {
    if (!isModified) return;
    const count = Object.keys(localChanges).length;
    if (count > 1 && !window.confirm(`${count} Änderung(en) speichern?`)) return;
    setIsSaving(true);
    try {
      for (const [field, value] of Object.entries(localChanges)) {
        await onUpdate(user.userId, field, value);
      }
      setLocalChanges({});
    } finally {
      setIsSaving(false);
    }
  };

  const deviceCount = user.devices ? Object.keys(user.devices).length : 0;

  return (
<div className="admin-dialog-overlay" onClick={onClose}>
      <div className="admin-dialog-content" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="admin-dialog-header">
          <div>
            <h3 className="admin-dialog-title">
              User: <span style={{ fontFamily: 'monospace', fontSize: '13px' }}>{user.userId.substring(0, 12)}...</span>
            </h3>
            <span className="admin-dialog-subtitle">{user.userId}</span>
          </div>
          <div className="admin-dialog-actions">
            <button
              className="admin-btn-danger-light"
              onClick={() => onDeleteUser(user)}
              disabled={isDeleting}
            >
              🗑️ Löschen
            </button>
            <button
              className="admin-btn-secondary"
              onClick={onClose}
            >
              ✕
            </button>
          </div>
        </div>

        {/* User Info */}
        <div className="admin-form-row" style={{ marginBottom: '20px' }}>
          <div className="admin-form-field">
            <label className="admin-form-label">Email</label>
            <input
              className={`admin-form-input ${(localChanges as any).email !== undefined ? 'modified' : ''}`}
              type="text"
              defaultValue={user.email || ''}
              onChange={e => handleLocalChange('email', e.target.value)}
              placeholder="Keine Email"
            />
          </div>
          <div className="admin-form-field">
            <label className="admin-form-label">Telegram ID</label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input
                className={`admin-form-input ${(localChanges as any).telegramId !== undefined ? 'modified' : ''}`}
                type="number"
                defaultValue={user.telegramId || ''}
                onChange={e => handleLocalChange('telegramId', e.target.value ? Number(e.target.value) : undefined)}
                style={{ flex: 1 }}
              />
              {user.telegramDisplayName && (
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', whiteSpace: 'nowrap' as const }}>
                  → {user.telegramDisplayName}
                </span>
              )}
            </div>
          </div>
          <div className="admin-form-field">
            <label className="admin-form-label">Tier</label>
            <select
              className={`admin-form-select ${(localChanges as any).tier !== undefined ? 'modified' : ''}`}
              value={localChanges.tier ?? user.tier}
              onChange={e => handleLocalChange('tier', e.target.value)}
            >
              <option value="free">Free</option>
              <option value="pro">Pro</option>
              <option value="premium">Premium</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="admin-form-field">
            <label className="admin-form-label">Premium Expires</label>
            <input
              className={`admin-form-input ${(localChanges as any).premiumExpiresAt !== undefined ? 'modified' : ''}`}
              type="date"
              value={
                localChanges.premiumExpiresAt !== undefined
                  ? (localChanges.premiumExpiresAt ? new Date(localChanges.premiumExpiresAt as number).toISOString().split('T')[0] : '')
                  : (user.premiumExpiresAt ? new Date(user.premiumExpiresAt).toISOString().split('T')[0] : '')
              }
              onChange={e => {
                const val = e.target.value;
                handleLocalChange('premiumExpiresAt', val ? new Date(val).getTime() : null);
              }}
            />
          </div>
        </div>

        {/* Read-only fields */}
        <div className="admin-info-section" style={{ marginBottom: '20px' }}>
          <div className="admin-info-item">
            <span className="admin-info-label">Erstellt</span>
            <span className="admin-info-value">{new Date(user.createdAt).toLocaleDateString('de-DE')}</span>
          </div>
          <div className="admin-info-item">
            <span className="admin-info-label">Aktualisiert</span>
            <span className="admin-info-value">{user.updatedAt ? new Date(user.updatedAt).toLocaleDateString('de-DE') : '—'}</span>
          </div>
          <div className="admin-info-item">
            <span className="admin-info-label">Passwort</span>
            <span className="admin-info-value">{user.hasPassword ? '✓ Gesetzt' : '✗ Nicht gesetzt'}</span>
          </div>
          <div className="admin-info-item">
            <span className="admin-info-label">Email verifiziert</span>
            <span className="admin-info-value">{user.emailVerified ? '✓ Ja' : '✗ Nein'}</span>
          </div>
          <div className="admin-info-item">
            <span className="admin-info-label">Geräte (Push)</span>
            <span className="admin-info-value">{deviceCount > 0 ? `${deviceCount} registriert` : 'Keine'}</span>
          </div>
        </div>

        {/* Save bar */}
        {isModified && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginBottom: '16px' }}>
            <button
              className="admin-btn-secondary"
              onClick={() => setLocalChanges({})}
            >
              Verwerfen
            </button>
            <button
              className="admin-btn-success"
              onClick={handleSave}
              disabled={isSaving}
            >
              💾 {isSaving ? 'Speichert...' : 'Speichern'}
            </button>
          </div>
        )}

        {/* Actions */}
        <div className="admin-action-group">
          <span className="admin-action-group-label">Aktionen</span>
          {user.email && (
            <button onClick={() => onResetPassword(user)} disabled={isResetting}
              className="admin-btn-utility" style={{ fontSize: '12px' }}>
              🔐 Passwort zurücksetzen
            </button>
          )}
          {deviceCount > 0 && (
            <button onClick={() => onTestPush(user.userId)} disabled={isSendingPush}
              className="admin-btn-utility" style={{ fontSize: '12px' }}>
              {isSendingPush ? '⏳' : '🔔'} Test Push
            </button>
          )}
          {user.email && (
            <button onClick={() => onTestEmail(user.userId)} disabled={isSendingEmail}
              className="admin-btn-utility" style={{ fontSize: '12px' }}>
              {isSendingEmail ? '⏳' : '📧'} Test Email
            </button>
          )}
          {user.telegramId && (
            <button onClick={() => onTestTelegram(user.userId)} disabled={isSendingTelegram}
              className="admin-btn-utility" style={{ fontSize: '12px' }}>
              {isSendingTelegram ? '⏳' : '📱'} Test Telegram
            </button>
          )}
          {user.email && (
            <button
              onClick={async () => {
                if (!window.confirm(`Email-Verifikation für ${user.email} zurücksetzen?`)) return;
                setIsResettingEmailVerif(true);
                try {
                  await onResetEmailVerification(user.userId);
                } finally {
                  setIsResettingEmailVerif(false);
                }
              }}
              disabled={isResettingEmailVerif}
              className="admin-btn-utility" style={{ fontSize: '12px' }}>
              {isResettingEmailVerif ? '⏳' : '🔄'} Email Verifikation zurücksetzen
            </button>
          )}
        </div>

        {/* Subscriptions */}
        <div>
          <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', color: 'var(--text-main)' }}>
            Suchen ({subscriptions.length})
          </h4>
          {/* Search filter */}
          {subscriptions.length > 0 && (
            <input
              type="text"
              placeholder="Filtern nach Zone, PLZ, Telegram-Titel oder -ID..."
              value={searchFilter}
              onChange={e => setSearchFilter(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 10px',
                marginBottom: '12px',
                fontSize: '12px',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                backgroundColor: 'var(--bg-secondary)',
                color: 'var(--text-main)',
                boxSizing: 'border-box',
              }}
            />
          )}
          {isLoadingSubs ? (
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '12px' }}>Lade Suchen...</div>
          ) : subscriptions.length === 0 ? (
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '12px' }}>Keine Suchen vorhanden</div>
          ) : filteredSubscriptions.length === 0 ? (
            <div style={{ fontSize: '13px', color: 'var(--text-muted)', fontStyle: 'italic', padding: '12px' }}>Keine Suchen mit dem Filter "{searchFilter}" gefunden</div>
          ) : (
            <table style={{ width: '100%', fontSize: '12px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border-color)', color: 'var(--text-muted)', fontSize: '10px', textTransform: 'uppercase' as const }}>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>ID</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Suchgebiet</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Telegram</th>
                  <th style={{ padding: '6px 8px', textAlign: 'left' }}>Kanäle</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}>Status</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}>Ablauf</th>
                  <th style={{ padding: '6px 8px', textAlign: 'center' }}></th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscriptions.map((sub: any) => (
                  <tr key={sub.subscriptionId} style={{
                    borderBottom: '1px solid var(--border-color)',
                    opacity: sub.deleted ? 0.4 : 1,
                  }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontSize: '10px', color: 'var(--text-muted)' }}>
                      {sub.subscriptionId?.replace('sub-', '').substring(0, 8)}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-main)' }}>
                      {(() => {
                        // Use zone labels (same logic as DashboardHome)
                        if (sub.uiMetadata?.zones && sub.uiMetadata.zones.length > 0) {
                          const zones = sub.uiMetadata.zones.map((z: any) => ({
                            i: z.i || z.id,
                            t: z.t || z.type,
                            l: z.l || z.label,
                          }));
                          const weights: Record<string, number> = { CI: 1, City: 1, DI: 2, District: 2, RE: 3, Region: 3, TO: 4, Town: 4, ZI: 10, Zip: 10 };
                          const sorted = [...zones].sort((a, b) => (weights[a.t] || 5) - (weights[b.t] || 5));
                          const names = sorted.slice(0, 3).map(z => z.l || z.i).join(', ');
                          const rest = sorted.length - 3;
                          return (
                            <span>
                              📍 {names}{rest > 0 && <span style={{ color: 'var(--text-muted)' }}> +{rest}</span>}
                              {sub.zipCodes?.length > 0 && <span style={{ color: 'var(--text-muted)', marginLeft: '4px' }}>({sub.zipCodes.length} PLZ)</span>}
                            </span>
                          );
                        }
                        // Fallback: show up to 3 ZIPs + count
                        if (sub.zipCodes?.length > 0) {
                          const shown = sub.zipCodes.slice(0, 3).join(', ');
                          const rest = sub.zipCodes.length - 3;
                          return (
                            <span>
                              📍 {shown}{rest > 0 && <span style={{ color: 'var(--text-muted)' }}> +{rest} weitere</span>}
                            </span>
                          );
                        }
                        return <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>;
                      })()}
                      {/* Filter summary */}
                      {(() => {
                        const f = sub.filters;
                        if (!f) return null;
                        const parts: string[] = [];
                        if (f.minRooms != null || f.maxRooms != null) {
                          parts.push(f.minRooms != null && f.maxRooms != null
                            ? `${f.minRooms}–${f.maxRooms} Zi.`
                            : f.minRooms != null ? `≥${f.minRooms} Zi.` : `≤${f.maxRooms} Zi.`);
                        }
                        if (f.maxRent != null) parts.push(`max. ${f.maxRent}€`);
                        else if (f.maxBaseRent != null) parts.push(`max. ${f.maxBaseRent}€ KM`);
                        if (parts.length === 0) return null;
                        return <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px' }}>{parts.join(' · ')}</div>;
                      })()}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-main)', fontSize: '11px' }}>
                      {sub.notificationTargets?.telegram ? (
                        <span>
                          <div>{sub.notificationTargets.telegram.chatTitle || 'Untitled'}</div>
                          <div style={{ color: 'var(--text-muted)', fontSize: '9px', fontFamily: 'monospace' }}>
                            ID: {sub.notificationTargets.telegram.chatId}
                          </div>
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', color: 'var(--text-main)' }}>
                      {(sub.notificationChannels || []).map((ch: string) => {
                        const icons: Record<string, string> = { email: '📧', telegram: '📱', push: '🔔' };
                        return <span key={ch} title={ch} style={{ marginRight: '4px' }}>{icons[ch] || ch}</span>;
                      })}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      <span className={`admin-status-badge ${sub.deleted ? 'deleted' : sub.enabled ? 'active' : 'paused'}`}>
                        {sub.deleted ? 'DEL' : sub.enabled ? 'AKTIV' : 'PAUSE'}
                      </span>
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center', fontSize: '11px' }}>
                      {sub.expiresAt ? (() => {
                        const daysLeft = Math.ceil((sub.expiresAt - Date.now()) / (24 * 60 * 60 * 1000));
                        const color = daysLeft <= 0 ? 'rgb(var(--c-danger-text))' : daysLeft <= 3 ? 'rgb(var(--c-warning-text))' : 'var(--text-muted)';
                        return (
                          <span style={{ display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'center' }}>
                            <span style={{ color }} title={new Date(sub.expiresAt).toLocaleString()}>
                              {daysLeft <= 0 ? 'abgelaufen' : `${daysLeft}d`}
                            </span>
                            <button
                              onClick={() => handleExpiryChange(sub.subscriptionId, null)}
                              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', padding: '0', color: 'var(--text-muted)' }}
                              title="Ablauf deaktivieren"
                            >✕</button>
                          </span>
                        );
                      })() : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                      {!sub.deleted && (
                        <button
                          onClick={() => onEditSubscription(sub)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', padding: '0 4px', color: 'var(--text-main)' }}
                          title="Bearbeiten"
                        >
                          ✏️
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};
